/**
 * The mice and the birds -- the visible half of the "hunt" row in
 * shared/happy.js, drawn from the server's word and nothing else.
 *
 * This is the butterflies' sibling with the authority inverted.  The
 * butterflies are client-side ambience whose pounces the server merely
 * counts; prey are CONTESTED -- two cats must not both catch one mouse -- so
 * the simulation lives in server/src/prey.js and this file is a renderer: it
 * receives rows at the snapshot cadence, smooths them, and animates what the
 * mode says is happening.  It never decides a position, a flee, a capture or
 * a delivery, which is why there is no gameplay number in this file at all
 * -- only sizes, colours and wingbeats.
 *
 * Smoothing is a plain exponential chase of the latest row rather than the
 * cats' bracket interpolation.  The cats earn interp.js because a walking
 * cat's gait is read against its ground speed and a stutter shows in the
 * legs; a mouse is a 6 cm blur whose whole vocabulary is "still" and
 * "bolting", and a 12 Hz chase of a 20 Hz stream is indistinguishable from
 * the real thing at that size.  The one case that would look wrong -- a
 * respawn teleporting a "smoothed" mouse across the garden -- is snapped
 * instead, gated on distance.
 *
 * Same scene bargain as the butterflies: not built through level/builder.js
 * (it merges and cannot animate), its own object3D beside the level, one
 * InstancedMesh per part -- mouse bodies, mouse tails, bird bodies, two wing
 * sides -- five draw calls for the whole population.  MeshBasicMaterial with
 * a baked vertical gradient in vertex colour: unlit so a dark mouse stays
 * legible on dark grass at dusk-fog range, gradient so it still reads as a
 * body and not a decal when the camera is on top of it.
 */
import * as THREE from "three";
import { PreyCol } from "@catworld/shared";
import { PREY_SPECIES, PREY_MODES } from "@catworld/shared";

/** Instance headroom per species.  The server ships 4 + 3 today; the caps
    exist so a population tweak is a server deploy, not a client one. */
const MAX_EACH = 8;

/**
 * Mice in door-mouse greys and browns; birds in sparrow browns with one
 * robin-adjacent rust -- which is one hue off the HUD's own accent, the same
 * small joke the orange butterfly tells, and the reason the "special" bird
 * never clashes with the UI announcing its capture.
 */
const MOUSE_COLOURS = [0x8f8478, 0x7b7168, 0x9a8d7f, 0x6f665e];
const BIRD_COLOURS = [0x9b8468, 0x8a745a, 0xa5906f, 0xb0603a];

/** How hard the render position chases the wire, 1/s.  12 puts the lag at
    ~80 ms -- one and a half snapshots, the same neighbourhood the cats'
    interpolation delay sits in, so a mouse fleeing a remote cat stays
    beside the cat chasing it instead of leading it. */
const CHASE = 12;
/** Past this, the move was a respawn or a capture, not motion: snap. */
const SNAP_DIST = 2.5;

/** Where a catch hangs: forward of the muzzle, at jaw height.  Matched by
    eye against the toon heads; the server glues its own copy of the carried
    position with the same forward offset so late joiners agree. */
const MOUTH_FWD = 0.26;
const MOUTH_UP = 0.14;

// ---------------------------------------------------------------------------
// Geometry.  Everything is built nose toward +z, because that is the cats'
// own convention (facing 0 walks +z, forward = (sin, cos)), and one
// convention for every heading in the scene is worth more than any shape.
// ---------------------------------------------------------------------------

/** Bake a belly-dark vertical gradient into vertex colour, so the unlit
    material still models form.  lo..hi is the geometry's own y span. */
function shade(geom, lo, hi) {
  const pos = geom.attributes.position;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = Math.min(1, Math.max(0, (pos.getY(i) - lo) / (hi - lo)));
    const v = 0.55 + 0.45 * t;
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = v;
  }
  geom.setAttribute("color", new THREE.BufferAttribute(col, 3));
  return geom;
}

/** A mouse: a 6.6 x 5.6 x 11 cm ellipsoid, belly on the ground.  Twice a
    real mouse for the butterflies' reason -- it has to read from a standing
    camera -- and the tail (its own instanced mesh) is what says "mouse"
    rather than "pebble". */
function mouseBodyGeometry() {
  const g = new THREE.SphereGeometry(0.05, 8, 6);
  g.scale(0.66, 0.56, 1.1);
  g.translate(0, 0.028, 0);
  return shade(g, 0, 0.056);
}

/** The tail: a 11 cm taper trailing -z, root at the rump, tip drooping to
    the grass.  Thick end at the body (radiusBottom), and the whole thing
    pre-tilted so the instance transform only has to add the wag. */
function mouseTailGeometry() {
  const g = new THREE.CylinderGeometry(0.0025, 0.005, 0.11, 5);
  g.rotateX(-Math.PI / 2);          // along -z
  g.rotateX(-0.28);                 // droop toward the ground
  g.translate(0, 0.03, -0.1);
  return shade(g, 0, 0.04);
}

/** A bird: an egg pitched slightly nose-up, 8 x 9 x 13 cm.  No head sphere
    and no beak -- at this size the peck animation carries "bird" better
    than geometry would, and every part is a draw call forever. */
function birdBodyGeometry() {
  const g = new THREE.SphereGeometry(0.05, 8, 6);
  g.scale(0.8, 0.9, 1.3);
  g.rotateX(-0.18);
  g.translate(0, 0.052, 0);
  return shade(g, 0, 0.1);
}

/** One wing, hinge at the origin, spanning +x -- the butterflies' template
    with a bird's planform: longer, narrower, swept.  Folded it lies along
    the flank; flapping it rolls about the body line exactly as theirs do. */
function birdWingGeometry() {
  const s = new THREE.Shape();
  s.moveTo(0, 0.012);
  s.quadraticCurveTo(0.06, 0.03, 0.115, 0.008);   // leading edge to the tip
  s.quadraticCurveTo(0.07, -0.02, 0.02, -0.022);  // trailing edge home
  s.lineTo(0, -0.008);
  const g = new THREE.ShapeGeometry(s, 4);
  const pos = g.attributes.position;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const v = 0.5 + 0.5 * Math.min(1, Math.max(0, (x - 0.004) / 0.03));
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = v;
  }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  g.rotateX(-Math.PI / 2);          // shape XY -> flight XZ, leading edge +z
  return g;
}

export class Prey {
  constructor() {
    const mat = () => new THREE.MeshBasicMaterial({
      vertexColors: true, side: THREE.DoubleSide,
    });
    this.mouseBody = new THREE.InstancedMesh(mouseBodyGeometry(), mat(), MAX_EACH);
    this.mouseTail = new THREE.InstancedMesh(mouseTailGeometry(), mat(), MAX_EACH);
    this.birdBody = new THREE.InstancedMesh(birdBodyGeometry(), mat(), MAX_EACH);
    const wingR = birdWingGeometry();
    const wingL = wingR.clone();
    wingL.scale(-1, 1, 1);
    this.birdWingR = new THREE.InstancedMesh(wingR, mat(), MAX_EACH);
    this.birdWingL = new THREE.InstancedMesh(wingL, mat(), MAX_EACH);

    this.meshes = [this.mouseBody, this.mouseTail,
                   this.birdBody, this.birdWingR, this.birdWingL];
    for (const m of this.meshes) {
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      // The butterflies' bargains, unchanged: instances roam metres from the
      // origin-sized geometry bounds, so honest culling would cull visible
      // prey; and shadow-casting critters at the shadow span's edge flicker
      // worse than shadowless ones look.
      m.frustumCulled = false;
      m.castShadow = false;
      m.receiveShadow = false;
    }

    this.group = new THREE.Group();
    this.group.add(...this.meshes);

    // Everything starts hidden; rows create entries as they arrive.
    this.zero = new THREE.Matrix4().makeScale(0, 0, 0);
    for (const m of this.meshes) {
      for (let i = 0; i < MAX_EACH; i++) m.setMatrixAt(i, this.zero);
      m.instanceMatrix.needsUpdate = true;
    }

    this.entries = new Map();         // prey id -> render state
    this.nextIdx = { mouse: 0, bird: 0 };

    // Same reduced-motion posture as the butterflies: calm, never gone --
    // the prey must stay findable, so only the flourish rates halve.
    this.calm = false;
    try {
      this.calm = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    } catch {}

    this.poses = null;                // bindPoses' hooks, for carried prey
    this.dummy = new THREE.Object3D();
    this.qYaw = new THREE.Quaternion();
    this.qTilt = new THREE.Quaternion();
    this.sampleOut = {};              // scratch for poses.sample
  }

  /** Add this to the scene; it is not part of the level build. */
  get object3D() {
    return this.group;
  }

  /**
   * How carried prey find their cat.  ownSlot/ownPose exist because the
   * local cat is drawn from the local Player, frames ahead of its own
   * snapshot -- gluing our catch to our snapshot row would trail the mouth
   * by a visible beat.  Everyone else's cat is drawn from sampleLive, so
   * their catch is glued to the same sample.
   */
  bindPoses({ ownSlot, ownPose, sample }) {
    this.poses = { ownSlot, ownPose, sample };
  }

  /** The latest prey rows off the wire (PreyCol order).  Trusted the way
      every server message is; the guards here are shape, not suspicion. */
  setRows(rows) {
    for (const row of rows) {
      if (!Array.isArray(row) || row.length < 8) continue;
      const id = row[PreyCol.ID];
      const species = PREY_SPECIES[row[PreyCol.SPECIES]] ?? "mouse";
      let e = this.entries.get(id);
      if (!e) {
        // First sight of this id: claim the next instance slot its species
        // has.  Ids are stable for the server process, so the mapping is too.
        const idx = this.nextIdx[species];
        if (idx >= MAX_EACH) continue;      // headroom exceeded: undrawn, not broken
        this.nextIdx[species] = idx + 1;
        e = {
          id, species, idx,
          cur: new THREE.Vector3(row[PreyCol.X], row[PreyCol.Y], row[PreyCol.Z]),
          tgt: new THREE.Vector3(),
          heading: row[PreyCol.HEADING],
          tgtHeading: row[PreyCol.HEADING],
          mode: "gone", carrier: -1,
          phase: Math.random() * Math.PI * 2,      // unsynchronised wags/flaps
          peckPhase: Math.random() * Math.PI * 2,
          size: 0.9 + Math.random() * 0.25,
          colour: null,
        };
        const c = new THREE.Color((species === "bird" ? BIRD_COLOURS : MOUSE_COLOURS)[
          idx % (species === "bird" ? BIRD_COLOURS.length : MOUSE_COLOURS.length)]);
        if (species === "bird") {
          this.birdBody.setColorAt(idx, c);
          this.birdWingR.setColorAt(idx, c);
          this.birdWingL.setColorAt(idx, c);
          for (const m of [this.birdBody, this.birdWingR, this.birdWingL]) {
            m.instanceColor.needsUpdate = true;
          }
        } else {
          this.mouseBody.setColorAt(idx, c);
          this.mouseTail.setColorAt(idx, c);
          this.mouseBody.instanceColor.needsUpdate = true;
          this.mouseTail.instanceColor.needsUpdate = true;
        }
        this.entries.set(id, e);
      }
      e.tgt.set(row[PreyCol.X], row[PreyCol.Y], row[PreyCol.Z]);
      e.tgtHeading = row[PreyCol.HEADING];
      e.mode = PREY_MODES[row[PreyCol.MODE]] ?? "lurk";
      e.carrier = row[PreyCol.CARRIER];
    }
  }

  /** The species this cat slot is carrying, or null -- for the HUD's
      "carry it home" line, so main.js never reads a wire column itself. */
  carriedBy(slot) {
    for (const e of this.entries.values()) {
      if (e.mode === "carried" && e.carrier === slot) return e.species;
    }
    return null;
  }

  update(dt) {
    dt = Math.min(dt || 0, 0.05);
    const calmF = this.calm ? 0.5 : 1;
    const k = 1 - Math.exp(-dt * CHASE);

    for (const e of this.entries.values()) {
      if (e.mode === "gone") {
        this.hide(e);
        continue;
      }

      let yaw = e.heading;
      let carriedTilt = 0;

      if (e.mode === "carried" && this.poses && e.carrier >= 0) {
        // In a mouth.  Crosswise -- a cat carries small prey across its jaws
        // -- and slightly rolled, which is all "limp" needs to read.
        const own = e.carrier === this.poses.ownSlot();
        const pose = own ? this.poses.ownPose()
                         : this.poses.sample(e.carrier, this.sampleOut);
        if (pose) {
          e.cur.set(
            pose.x + Math.sin(pose.facing) * MOUTH_FWD,
            pose.y + MOUTH_UP,
            pose.z + Math.cos(pose.facing) * MOUTH_FWD,
          );
          yaw = e.heading = pose.facing + Math.PI / 2;
          carriedTilt = 0.4;
        } else {
          e.cur.lerp(e.tgt, k);       // no pose yet: the server's glue will do
        }
      } else {
        // Free prey: chase the wire, snap across respawns.
        if (e.cur.distanceToSquared(e.tgt) > SNAP_DIST * SNAP_DIST) {
          e.cur.copy(e.tgt);
          e.heading = e.tgtHeading;
        } else {
          e.cur.lerp(e.tgt, k);
          e.heading += shortestArc(e.heading, e.tgtHeading) * Math.min(1, dt * 10);
        }
        yaw = e.heading;
      }

      if (e.species === "mouse") this.poseMouse(e, dt, calmF, yaw, carriedTilt);
      else this.poseBird(e, dt, calmF, yaw, carriedTilt);
    }

    for (const m of this.meshes) m.instanceMatrix.needsUpdate = true;
  }

  hide(e) {
    if (e.species === "bird") {
      this.birdBody.setMatrixAt(e.idx, this.zero);
      this.birdWingR.setMatrixAt(e.idx, this.zero);
      this.birdWingL.setMatrixAt(e.idx, this.zero);
    } else {
      this.mouseBody.setMatrixAt(e.idx, this.zero);
      this.mouseTail.setMatrixAt(e.idx, this.zero);
    }
  }

  poseMouse(e, dt, calmF, yaw, carriedTilt) {
    // The scurry: fleeing and darting run the wag and a tiny gallop-bob;
    // lurking barely moves it -- the stillness IS the tell that it can be
    // crept up on, so the idle wag is nearly subliminal.
    const moving = e.mode === "dart" || e.mode === "flee";
    const rate = (e.mode === "flee" ? 18 : moving ? 12 : 1.4) * calmF;
    e.phase += rate * dt;
    const bob = moving ? Math.abs(Math.sin(e.phase * 2)) * 0.008 : 0;
    const wag = Math.sin(e.phase) * (moving ? 0.35 : 0.08);

    const d = this.dummy;
    d.position.set(e.cur.x, e.cur.y + bob, e.cur.z);
    d.scale.setScalar(e.size);
    this.qYaw.setFromAxisAngle(AXIS_Y, yaw);
    this.qTilt.setFromAxisAngle(AXIS_Z, carriedTilt);
    d.quaternion.copy(this.qYaw).multiply(this.qTilt);
    d.updateMatrix();
    this.mouseBody.setMatrixAt(e.idx, d.matrix);

    // The tail shares the body transform plus its own yaw of wag, so it
    // sweeps behind the rump rather than orbiting the whole mouse.
    this.qTilt.setFromAxisAngle(AXIS_Y, wag);
    d.quaternion.multiply(this.qTilt);
    d.updateMatrix();
    this.mouseTail.setMatrixAt(e.idx, d.matrix);
  }

  poseBird(e, dt, calmF, yaw, carriedTilt) {
    // Flight is the only flapping state; on the ground the wings fold to the
    // flanks and the peck cycle carries the character.  The peck is a
    // clipped sine cubed: mostly level, then three quick dips -- the rhythm
    // of a real feeding bird, not a metronome.
    const flying = e.mode === "flee";
    e.phase += (flying ? 7 : 0.5) * Math.PI * 2 * calmF * dt;
    e.peckPhase += 1.1 * calmF * dt * Math.PI * 2;
    const peck = !flying && e.mode === "lurk"
      ? Math.max(0, Math.sin(e.peckPhase)) ** 3 * 0.55
      : 0;
    const hop = e.mode === "dart" ? Math.abs(Math.sin(e.phase * 12)) * 0.01 : 0;

    const d = this.dummy;
    d.position.set(e.cur.x, e.cur.y + hop, e.cur.z);
    d.scale.setScalar(e.size);
    this.qYaw.setFromAxisAngle(AXIS_Y, yaw);
    this.qTilt.setFromAxisAngle(AXIS_X, peck + carriedTilt);
    d.quaternion.copy(this.qYaw).multiply(this.qTilt);
    d.updateMatrix();
    this.birdBody.setMatrixAt(e.idx, d.matrix);

    // Wing roll about the body line: airborne it beats 0.9 rad each way
    // around a raised rest; grounded (or carried) it folds hard down.
    const flap = flying ? 0.35 + Math.sin(e.phase) * 0.9 : -1.15;
    d.position.y += 0.055 * e.size;   // shoulder line, above the body centre
    this.qTilt.setFromAxisAngle(AXIS_Z, flap);
    d.quaternion.copy(this.qYaw).multiply(this.qTilt);
    d.updateMatrix();
    this.birdWingR.setMatrixAt(e.idx, d.matrix);
    this.qTilt.setFromAxisAngle(AXIS_Z, -flap);
    d.quaternion.copy(this.qYaw).multiply(this.qTilt);
    d.updateMatrix();
    this.birdWingL.setMatrixAt(e.idx, d.matrix);
  }
}

function shortestArc(from, to) {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

const AXIS_X = new THREE.Vector3(1, 0, 0);
const AXIS_Y = new THREE.Vector3(0, 1, 0);
const AXIS_Z = new THREE.Vector3(0, 0, 1);
